import { useEffect, useMemo, useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { Plus, Trash2 } from 'lucide-react'
import { Api } from '../lib/api.js'
import { useAuth } from '../contexts/AuthContext.jsx'
import { env } from '../config/env.js'
import Button from '../components/ui/Button.jsx'
import Card from '../components/ui/Card.jsx'
import OverQuotaModal from '../components/shell/OverQuotaModal.jsx'
import SessionsToolbar from '../components/sessions/SessionsToolbar.jsx'
import { timeAgo } from '../lib/timeAgo.js'

const KIND_LABELS = { decouverte: 'Découverte', bilan_annuel: 'Bilan annuel', suivi: 'Suivi', recommandation: 'Recommandation' }
const STATE_LABELS = { recording: 'En cours', processing: 'Traitement', ready: 'Prêt', signed: 'Signé', aborted: 'Interrompue', archived: 'Archivée' }
const STATE_TONES = {
  recording:  'text-terra-600',
  processing: 'text-amber-700',
  ready:      'text-ink',
  signed:     'text-ink',
  aborted:    'text-muted italic',
  archived:   'text-muted',
}

const norm = (s) => (s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')

function clientNameFor(session, clientMap) {
  const cid = session.ClientID || session.client_id
  if (!cid) return 'Découverte (sans client)'
  return clientMap[cid] || 'Client supprimé'
}

export default function SessionsListPage() {
  const { token, onUnauthorized, usage, refreshUsage, quotaTriggered, clearQuotaTriggered } = useAuth()
  const nav = useNavigate()
  const [sessions, setSessions] = useState(null)
  const [clientMap, setClientMap] = useState({})
  const [err, setErr] = useState(null)

  // Toolbar state.
  const [query, setQuery] = useState('')
  const [kinds, setKinds] = useState([])
  const [states, setStates] = useState([])
  const [groupByClient, setGroupByClient] = useState(false)

  useEffect(() => {
    const api = new Api({ baseURL: env.API_BASE_URL || '', getToken: () => token, onUnauthorized })
    api.get('/api/clarity/sessions').then(setSessions).catch((e) => setErr(e.message))
    api.listClients().then((list) => {
      const map = {}
      for (const c of list || []) map[c.ID || c.id] = c.Name || c.name
      setClientMap(map)
    }).catch(() => { /* clients page surfaces this error */ })
    refreshUsage?.()
  }, [token, onUnauthorized, refreshUsage])

  const toggleKind = (k) => setKinds((ks) => ks.includes(k) ? ks.filter((x) => x !== k) : [...ks, k])
  const toggleState = (s) => setStates((ss) => ss.includes(s) ? ss.filter((x) => x !== s) : [...ss, s])

  // Track which row is mid-delete so the icon can show a busy state and we
  // can ignore double-clicks on the same row.
  const [deletingId, setDeletingId] = useState(null)
  const onDeleteRow = async (sess) => {
    const sid = sess.ID || sess.id
    const cname = clientNameFor(sess, clientMap)
    if (!window.confirm(`Supprimer la séance « ${cname} » ? Cette action est définitive.`)) return
    setDeletingId(sid)
    const api = new Api({ baseURL: env.API_BASE_URL || '', getToken: () => token, onUnauthorized })
    try {
      await api.deleteSession(sid)
      setSessions((prev) => (prev || []).filter((s) => (s.ID || s.id) !== sid))
    } catch (e) {
      console.error('delete session:', e)
      alert('La suppression a échoué — réessayez ou consultez les logs.')
    } finally {
      setDeletingId(null)
    }
  }

  const filtered = useMemo(() => {
    if (!sessions) return null
    const q = norm(query)
    return sessions.filter((s) => {
      const kind = s.Kind || s.kind
      const state = s.State || s.state
      if (kinds.length > 0 && !kinds.includes(kind)) return false
      if (states.length > 0 && !states.includes(state)) return false
      if (q) {
        const cname = norm(clientNameFor(s, clientMap))
        if (!cname.includes(q)) return false
      }
      return true
    })
  }, [sessions, query, kinds, states, clientMap])

  const grouped = useMemo(() => {
    if (!groupByClient || !filtered) return null
    const m = new Map()
    for (const s of filtered) {
      const key = clientNameFor(s, clientMap)
      if (!m.has(key)) m.set(key, [])
      m.get(key).push(s)
    }
    return Array.from(m.entries()).sort(([a], [b]) => {
      if (a.startsWith('Découverte')) return 1
      if (b.startsWith('Découverte')) return -1
      return a.localeCompare(b, 'fr')
    })
  }, [filtered, groupByClient, clientMap])

  const renderRow = (s) => {
    const sid = s.ID || s.id
    const state = s.State || s.state
    const isAborted = state === 'aborted'
    const navigable = state === 'ready' || state === 'signed'
    const isDeleting = deletingId === sid
    return (
      <li key={sid}>
        <div
          className={`group relative flex items-stretch rounded-xl border bg-white hover:border-terra-500 ${
            isAborted ? 'border-line/60 opacity-70' : 'border-line'
          }`}
        >
          <Link
            to={navigable ? `/sessions/${sid}` : '#'}
            className="flex flex-1 items-center justify-between gap-2 px-4 py-3.5 sm:px-5"
          >
            <div className="min-w-0">
              <div className="truncate text-[14px] font-semibold">{clientNameFor(s, clientMap)}</div>
              <div className="truncate text-[11.5px] uppercase tracking-[.4px] text-muted">
                {KIND_LABELS[s.Kind || s.kind] || s.Kind || 'Séance'} · {timeAgo(s.StartedAt || s.started_at)}
              </div>
            </div>
            <div className={`shrink-0 text-[11px] ${STATE_TONES[state] || 'text-muted'}`}>
              {STATE_LABELS[state] || state}
            </div>
          </Link>
          <button
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onDeleteRow(s) }}
            disabled={isDeleting}
            title="Supprimer cette séance"
            className="mr-2 flex shrink-0 items-center justify-center rounded-lg px-2.5 text-muted opacity-0 transition-opacity hover:bg-terra-50 hover:text-terra-600 group-hover:opacity-100 focus:opacity-100 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <Trash2 size={14} strokeWidth={2} />
          </button>
        </div>
      </li>
    )
  }

  return (
    <div className="px-4 py-6 sm:px-7 sm:py-8 md:px-12 lg:px-20">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-[22px] font-semibold tracking-[-0.02em]">Séances</h1>
        <Button onClick={() => nav('/sessions/new')}>
          <Plus size={14} strokeWidth={2.4} /> Nouvelle séance
        </Button>
      </div>

      {err && <div className="mb-4 rounded-lg border border-terra-500/40 bg-terra-50 px-4 py-3 text-[12.5px] text-terra-600">{err}</div>}
      {sessions === null && <div className="text-[12.5px] text-muted">Chargement…</div>}

      {sessions?.length > 0 && (
        <SessionsToolbar
          query={query} onQueryChange={setQuery}
          kinds={kinds} onToggleKind={toggleKind}
          states={states} onToggleState={toggleState}
          groupByClient={groupByClient} onToggleGroupByClient={setGroupByClient}
        />
      )}

      {sessions?.length === 0 && (
        <Card className="px-8 py-10 text-center">
          <p className="text-[14px] font-medium">Aucune séance pour l'instant.</p>
          <p className="mt-1 text-[12.5px] text-muted">Cliquez sur « Nouvelle séance » pour commencer.</p>
        </Card>
      )}

      {filtered?.length === 0 && sessions?.length > 0 && (
        <Card className="px-8 py-10 text-center">
          <p className="text-[14px] font-medium">Aucune séance ne correspond aux filtres.</p>
        </Card>
      )}

      {!groupByClient && filtered?.length > 0 && (
        <ul className="space-y-2">{filtered.map(renderRow)}</ul>
      )}

      {groupByClient && grouped && grouped.map(([clientName, items]) => (
        <div key={clientName} className="mb-5">
          <div className="mb-2 flex items-center gap-2 text-[12px] uppercase tracking-[1.2px] text-muted">
            <span>{clientName}</span>
            <span className="rounded-full bg-line px-2 py-0.5 text-[10.5px] text-ink">{items.length}</span>
          </div>
          <ul className="space-y-2">{items.map(renderRow)}</ul>
        </div>
      ))}

      {quotaTriggered && usage && (
        <OverQuotaModal usage={usage} onClose={clearQuotaTriggered} />
      )}
    </div>
  )
}
